// src/App.tsx
import { Route, Routes } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";

import "./assets/scss/main.scss";
import "@assets/css/skeleton.css";

// Guards
import ProtectedRoute from "@modules/auth/guards/auth.guard";
import PublicRoute from "@modules/auth/guards/public-auth.guard";

// Firebase
import { listenNotification } from "@modules/shared/firebase/firebase";
import Report from "@modules/reports/pages/report";
import BasicMainLayout from "layouts/BasicMainLayout";
import PageLayoutRoute from "routes/PageLayoutRoute";
import { GymPartnerProvider } from "@modules/gyms/contexts/gym.context";
import TrainerPackageVerificationPage from "@modules/verification/trainer-package/pages/TrainerPackageVerificationPage";
import TrainerVerificationPage from "@modules/verification/trainer/pages/TrainerVerificationPage";
import OpenAISetting from "@modules/system-setting/pages/openAI-setting";
import MongoDBSetting from "@modules/system-setting/pages/mongodb-settings";
import VietGuysSetting from "@modules/system-setting/pages/vietguys-settings";
import FeatureManagement from "@modules/feature-flag/pages/feature-flag";
import AppVersionForceUpdate from "@modules/feature-flag/pages/app-version";

import ProductDetailPage from "@modules/products/components/ProductDetailPage";

import { BrandProvider } from "@modules/brands/contexts/BrandContext";
import BranchDetailPage from "./modules/gyms/pages/BranchDetailPage";

// Lazy imports
const Dashboard = lazy(() => import("./modules/dashboard/pages/dashboard"));
const Trainers = lazy(() => import("./modules/trainers/pages/trainers"));
const Login = lazy(() => import("./modules/auth/pages/login"));
const Signup = lazy(() => import("./modules/auth/pages/signup"));
const ForgotPassword = lazy(
  () => import("./modules/auth/pages/forgot-password"),
);
const ErrorPage = lazy(() => import("./components/shared/Error/Error"));
const CampaignsPage = lazy(
  () => import("@modules/engagements/campaigns/pages/campaigns-page"),
);
const RewardsPage = lazy(
  () => import("@modules/engagements/rewards/pages/rewards-page"),
);
const Clients = lazy(() => import("@modules/clients/pages/client"));

const AdminPost = lazy(() => import("@modules/posts/pages/admin-post"));
const PostDetail = lazy(() => import("@modules/posts/pages/post-detail"));
const Term = lazy(() => import("@modules/policies/pages/term"));
const Privacy = lazy(() => import("@modules/policies/pages/privacy"));
const MediaOnboarding = lazy(
  () => import("@modules/media/pages/media-onboarding"),
);
const MediaSystem = lazy(() => import("@modules/media/pages/media-system"));
const MediaDetail = lazy(() => import("@modules/media/pages/media-detail"));
const TopTrainerManager = lazy(
  () => import("@modules/trainers/pages/top-trainer"),
);
const TnProfile = lazy(() => import("@modules/profiles/pages/tnprofile"));
const PostReportDetail = lazy(
  () => import("@modules/reports/pages/report-detail"),
);

// Gym Partner
const GymBranding = lazy(() => import("@modules/gyms/pages/GymBrandPage"));
const GymBrandDetailPage = lazy(
  () => import("@modules/gyms/pages/GymBrandDetailPage"),
);

const BrandPage = lazy(() => import("@modules/brands/pages/BrandPage"));
const BrandDetailPage = lazy(
  () => import("@modules/brands/pages/BrandDetailPage"),
);

const StoreManagementPage = lazy(
  () => import("@modules/stores/pages/StorePage"),
);
const StoreDetailPage = lazy(
  () => import("@modules/stores/pages/StoreDetailPage"),
);

function App() {
  useEffect(() => {
    listenNotification();
  }, []);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route
            element={
              <GymPartnerProvider>
                <BrandProvider>
                  <BasicMainLayout />
                </BrandProvider>
              </GymPartnerProvider>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />

            <Route element={<PageLayoutRoute />}>
              <Route path="/trainers/:id" element={<TnProfile />} />
              <Route path="/trainers" element={<Trainers />} />
              <Route path="/top-trainers" element={<TopTrainerManager />} />
              <Route
                path="/engagements/campaigns"
                element={<CampaignsPage />}
              />
              <Route path="/engagements/rewards" element={<RewardsPage />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/admin-post" element={<AdminPost />} />
              <Route path="/onboarding-media" element={<MediaOnboarding />} />
              <Route path="/system-media" element={<MediaSystem />} />
              <Route path="/media-detail" element={<MediaDetail />} />
              <Route path="/post-detail" element={<PostDetail />} />
              <Route path="/term-policy" element={<Term />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/report" element={<Report />} />
              <Route path="/report/:id" element={<PostReportDetail />} />
              {/* Verification */}
              <Route
                path="/trainer-verification"
                element={<TrainerVerificationPage />}
              />
              <Route
                path="/trainer-package-verification"
                element={<TrainerPackageVerificationPage />}
              />
              {/* Setting */}
              <Route
                path="/system-setting/openAI-assistant"
                element={<OpenAISetting />}
              />
              <Route
                path="/system-setting/mongodb"
                element={<MongoDBSetting />}
              />
              <Route
                path="/system-setting/vietguys"
                element={<VietGuysSetting />}
              />
              <Route path="/b2b/brands" element={<BrandPage />} />
              <Route path="/b2b/brands/:id" element={<BrandDetailPage />} />
              <Route path="/b2b/stores" element={<StoreManagementPage />} />
              <Route path="/b2b/stores/:id" element={<StoreDetailPage />} />
              <Route
                path="/b2b/stores/:storeId/products/:productId"
                element={<ProductDetailPage />}
              />
              {/* Gym */}
              <Route path="/b2b/gyms" element={<GymBranding />} />
              <Route path="/b2b/gyms/:id" element={<GymBrandDetailPage />} />
              <Route
                path="/b2b/gyms/branch/:branchId"
                element={<BranchDetailPage />}
              />
              <Route
                path="/b2b/gyms/:id/branches/:branchId"
                element={<BranchDetailPage />}
              />

              {/* Feature Flag */}
              <Route path="/feature-flag" element={<FeatureManagement />} />
              <Route
                path="/app-version"
                element={<AppVersionForceUpdate />}
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
